import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { FileCheck, AlertCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";

import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { REPORT_CATEGORIES, ReportCategory } from "@/lib/reportOptionsData";

interface SubmitReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: {
    id: string;
    title?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  };
  /** When provided, the report covers a single itinerary component instead of the whole programme */
  itineraryItem?: {
    id: string;
    title?: string | null;
    date?: string | null;
  } | null;
  onSuccess?: () => void;
}


interface ReportFormData {
  timing_rating: string;
  engagement_rating: string;
  transport_rating: string;
  satisfaction_rating: string;
  guide_rating: string;
  issues_status: string;
  client_feedback: string;
  guide_feedback: string;
  additional_notes: string;
  transport_na: boolean;
}

export function SubmitReportDialog({
  open,
  onOpenChange,
  lead,
  itineraryItem,
  onSuccess,
}: SubmitReportDialogProps) {
  const queryClient = useQueryClient();
  const isComponentReport = !!itineraryItem?.id;
  const [formData, setFormData] = useState<ReportFormData>({
    timing_rating: "",
    engagement_rating: "",
    transport_rating: "",
    satisfaction_rating: "",
    guide_rating: "",
    issues_status: "",
    client_feedback: "",
    guide_feedback: "",
    additional_notes: "",
    transport_na: false,
  });

  const submitReportMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Insert the report with a share token
      const shareToken = crypto.randomUUID();
      const { error: reportError } = await supabase
        .from("lead_reports")
        .insert({
          lead_id: lead.id,
          itinerary_item_id: itineraryItem?.id ?? null,
          timing_rating: formData.timing_rating || null,
          engagement_rating: formData.engagement_rating || null,
          transport_rating: formData.transport_na ? "n/a" : (formData.transport_rating || null),
          satisfaction_rating: formData.satisfaction_rating || null,
          guide_rating: formData.guide_rating || null,
          issues_status: formData.issues_status || null,
          client_feedback: formData.client_feedback.trim() || null,
          guide_feedback: formData.guide_feedback.trim() || null,
          additional_notes: formData.additional_notes.trim() || null,
          submitted_by: user.id,
          share_token: shareToken,
        });

      if (reportError) throw reportError;

      // Only the overall programme wrap-up report completes the lead
      if (!isComponentReport) {
        const { error: leadError } = await supabase
          .from("leads")
          .update({ status: "completed" })
          .eq("id", lead.id);

        if (leadError) throw leadError;
      }

      // Log activity
      await supabase
        .from("activity_log")
        .insert({
          lead_id: lead.id,
          user_id: user.id,
          action: isComponentReport ? "component_report_submitted" : "report_submitted",
          details: isComponentReport
            ? `Component report submitted for "${itineraryItem?.title || "itinerary item"}"`
            : "Activity report submitted and lead marked as completed",
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-for-execution", lead.id] });
      queryClient.invalidateQueries({ queryKey: ["cta-executions"] });
      queryClient.invalidateQueries({ queryKey: ["lead-report", lead.id] });
      queryClient.invalidateQueries({ queryKey: ["lead-wrapup-report", lead.id] });
      queryClient.invalidateQueries({ queryKey: ["lead-programme-report", lead.id] });
      queryClient.invalidateQueries({ queryKey: ["lead-component-reports", lead.id] });
      toast({
        title: "Report Submitted",
        description: isComponentReport
          ? `Report saved for "${itineraryItem?.title || "this component"}".`
          : "The activity report has been submitted and the lead marked as completed.",
      });

      onOpenChange(false);
      onSuccess?.();
      // Reset form
      setFormData({
        timing_rating: "",
        engagement_rating: "",
        transport_rating: "",
        satisfaction_rating: "",
        guide_rating: "",
        issues_status: "",
        client_feedback: "",
        guide_feedback: "",
        additional_notes: "",
        transport_na: false,
      });
    },
    onError: (error: any) => {
      console.error("Error submitting report:", error);
      const duplicate = error?.code === "23505";
      toast({
        title: duplicate ? "Already reported" : "Error",
        description: duplicate
          ? (isComponentReport
              ? "A report already exists for this activity. Open View Report to see or edit it."
              : "An overall report already exists for this programme. Open View Report to see or edit it.")
          : `Failed to submit report: ${error?.message ?? "please try again."}`,
        variant: "destructive",
      });
    },
  });

  const handleRatingChange = (categoryId: string, value: string) => {
    const fieldMap: Record<string, keyof ReportFormData> = {
      timing: "timing_rating",
      engagement: "engagement_rating",
      transport: "transport_rating",
      satisfaction: "satisfaction_rating",
      guide_rating: "guide_rating",
      issues: "issues_status",
    };
    const field = fieldMap[categoryId];
    if (field) {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const getRatingValue = (categoryId: string): string => {
    const fieldMap: Record<string, keyof ReportFormData> = {
      timing: "timing_rating",
      engagement: "engagement_rating",
      transport: "transport_rating",
      satisfaction: "satisfaction_rating",
      guide_rating: "guide_rating",
      issues: "issues_status",
    };
    const field = fieldMap[categoryId];
    return field ? (formData[field] as string) : "";
  };

  const isFormValid = () => {
    // Require at least timing and engagement ratings
    return formData.timing_rating && formData.engagement_rating;
  };

  const renderCategorySelector = (category: ReportCategory) => {
    const isTransport = category.id === "transport";
    const isDisabled = isTransport && formData.transport_na;

    return (
      <div key={category.id} className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <Label className="text-sm font-medium">{category.name}</Label>
            <p className="text-xs text-muted-foreground">{category.description}</p>
          </div>
          {category.allowNA && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${category.id}-na`}
                checked={formData.transport_na}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    transport_na: !!checked,
                    transport_rating: checked ? "" : prev.transport_rating,
                  }))
                }
              />
              <Label htmlFor={`${category.id}-na`} className="text-xs text-muted-foreground">
                N/A (no transport)
              </Label>
            </div>
          )}
        </div>

        <RadioGroup
          value={getRatingValue(category.id)}
          onValueChange={(value) => handleRatingChange(category.id, value)}
          className="flex flex-wrap gap-2"
          disabled={isDisabled}
        >
          {category.options.map((option) => (
            <div key={option.id} className="flex items-center">
              <RadioGroupItem
                value={option.value}
                id={option.id}
                className="peer sr-only"
                disabled={isDisabled}
              />
              <Label
                htmlFor={option.id}
                className={`px-3 py-1.5 text-xs rounded-full border cursor-pointer transition-colors
                  ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}
                  peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary
                  hover:bg-muted
                `}
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            {isComponentReport ? "Submit Component Report" : "Submit Activity Report"}
          </DialogTitle>
          <DialogDescription>
            {isComponentReport ? (
              <>
                {itineraryItem?.title || "Itinerary component"}
                {itineraryItem?.date && ` • ${format(new Date(itineraryItem.date), "MMM dd, yyyy")}`}
                {lead.title && ` — ${lead.title}`}
              </>
            ) : (
              <>
                {lead.title}
                {lead.end_date && ` • ${format(new Date(lead.end_date), "MMM dd, yyyy")}`}
              </>
            )}
          </DialogDescription>

        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-4">
          <div className="space-y-6">
            {/* Required ratings notice */}
            <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span className="text-muted-foreground">
                Timing and Quality of Engagement are required fields.
              </span>
            </div>

            {/* Rating categories */}
            {REPORT_CATEGORIES.map((category) => (
              <div key={category.id}>
                {renderCategorySelector(category)}
                <Separator className="mt-4" />
              </div>
            ))}

            {/* Client Feedback */}
            <div className="space-y-2">
              <Label htmlFor="client_feedback">Client Feedback (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Direct quotes or feedback from the clients
              </p>
              <Textarea
                id="client_feedback"
                value={formData.client_feedback}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, client_feedback: e.target.value }))
                }
                placeholder='"The tour was fantastic, we learned so much about the local culture..."'
                className="min-h-[96px] max-h-[45vh] resize-y overflow-y-auto"
              />
            </div>

            {/* Guide Feedback */}
            <div className="space-y-2">
              <Label htmlFor="guide_feedback">Guide Feedback (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Feedback from or about the guide — performance, client rapport, logistics
              </p>
              <Textarea
                id="guide_feedback"
                value={formData.guide_feedback}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, guide_feedback: e.target.value }))
                }
                placeholder="The guide handled the group exceptionally well, especially during..."
                className="min-h-[96px] max-h-[45vh] resize-y overflow-y-auto"
              />
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="additional_notes">Additional Notes (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Any other context or observations from the activity
              </p>
              <Textarea
                id="additional_notes"
                value={formData.additional_notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, additional_notes: e.target.value }))
                }
                placeholder="Client showed particular interest in local architecture..."
                className="min-h-[96px] max-h-[45vh] resize-y overflow-y-auto"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => submitReportMutation.mutate()}
            disabled={!isFormValid() || submitReportMutation.isPending}
          >
            {submitReportMutation.isPending ? "Submitting..." : "Submit Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
